import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as onboardingApi from '../services/onboardingApi';

// Async thunks
export const fetchOnboarding = createAsyncThunk(
  'onboarding/fetch',
  async ({ onboardingId, token }, { rejectWithValue }) => {
    try {
      const data = await onboardingApi.fetchOnboarding(onboardingId, token);
      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const transitionState = createAsyncThunk(
  'onboarding/transition',
  async ({ onboardingId, action, data, token }, { rejectWithValue, dispatch }) => {
    try {
      const response = await onboardingApi.transitionOnboarding(onboardingId, action, data);
      return response;
    } catch (error) {
      if (error.message?.includes('Invalid action') && token) {
        dispatch(fetchOnboarding({ onboardingId, token }));
      }
      return rejectWithValue({ message: error.message });
    }
  }
);

export const navigateToState = createAsyncThunk(
  'onboarding/navigate',
  async ({ onboardingId, targetState }, { rejectWithValue }) => {
    try {
      const response = await onboardingApi.navigateToState(onboardingId, targetState);
      return response;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const saveOnboardingData = createAsyncThunk(
  'onboarding/save',
  async ({ onboardingId, field, value, token }, { rejectWithValue }) => {
    try {
      const response = await onboardingApi.patchOnboarding(onboardingId, { [field]: value }, token);
      return response;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const saveAnswer = createAsyncThunk(
  'onboarding/saveAnswer',
  async ({ onboardingId, questionId, answer }, { rejectWithValue }) => {
    try {
      const response = await onboardingApi.saveAnswer(onboardingId, questionId, answer);
      return { questionId, answer };
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const updateTask = createAsyncThunk(
  'onboarding/updateTask',
  async ({ onboardingId, taskId, status, progress }, { rejectWithValue }) => {
    try {
      const response = await onboardingApi.updateTaskStatus(onboardingId, taskId, status, progress);
      return { taskId, status, progress, overallProgress: response.overallProgress };
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const updateProjectTask = createAsyncThunk(
  'onboarding/updateProjectTask',
  async ({ onboardingId, token, projectId, taskId, updates }, { rejectWithValue }) => {
    try {
      const updated = await onboardingApi.updateProjectTask(
        onboardingId,
        projectId,
        taskId,
        updates,
        token
      );
      return { projectId, task: updated };
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const updateProjectLinks = createAsyncThunk(
  'onboarding/updateProjectLinks',
  async ({ projectId = null, taskId = null, links = [] }, { rejectWithValue }) => {
    try {
      const data = await onboardingApi.adminUpdateProjectLinks({ projectId, taskId, links });
      return { projectId, taskId, data };
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const fetchServiceTickets = createAsyncThunk(
  'onboarding/fetchServiceTickets',
  async ({ onboardingId, token, clientBusinessId }, { rejectWithValue }) => {
    try {
      const tickets = await onboardingApi.listServiceTickets(onboardingId, token, {}, clientBusinessId);
      return Array.isArray(tickets) ? tickets : [];
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const refreshProjectChildren = createAsyncThunk(
  'onboarding/refreshProjectChildren',
  async ({ projectId, onboardingId, clientBusinessId, token }, { rejectWithValue }) => {
    try {
      const data = await onboardingApi.fetchProjectChildren(projectId, { onboardingId, clientBusinessId, token });
      return { projectId, ...data };
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const createServiceTicket = createAsyncThunk(
  'onboarding/createServiceTicket',
  async ({ onboardingId, token, payload }, { rejectWithValue }) => {
    try {
      const created = await onboardingApi.createServiceTicket(onboardingId, payload, token);
      return created;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

const initialState = {
  // Core data
  onboardingId: null,
  currentState: null,
  maxReachedState: null,

  // Accès / authentification
  locked: false,
  authMethod: null,
  businessId: null,
  hasAccount: false,
  
  // User & Company data
  userData: null,
  companyData: {
    name: '',
  },
  // Stage-specific data
  qualification: {
    sector: null,
    employeeCount: null,
    challenges: [],
    additionalNeeds: '',
  },
  diagnostic: {
    scope: [],
    currentQuestionIndex: 0,
    answers: {},
    results: null,
  },
  blueprint: {
    modules: [],
    integrations: [],
  },
  structures: [],
  modules: [],
  automations: [],
  elements: [],
  integrations: [],
  // Portail client: projets BOS (grids) liés
  projects: [],
  projectIds: [],
  mainProjectId: null,
  locations: [],
  pdfs: [],
  tickets: [],
  ticketsLoading: false,
  nau: null,
  pricing: {
    includedModules: [],
    optionalModules: [],
    selectedAddons: [],
    setupPrice: 0,
    monthlyPrice: 0,
  },
  signature: {
    contract: null,
    consents: { cgv: false, dataProcessing: false },
    signed: false,
  },
  progress: {
    tasks: [],
    overallProgress: 0,
  },
  optimization: {
    recommendations: [],
    usageScore: 0,
  },
  governance: {
    roles: [],
    workflows: [],
    compliance: [],
  },
  completion: null,
  
  // Payment & Session (for diagnostic tiers)
  payment: null,
  session: null,
  
  // Branding colors
  branding: {
    mainColor: '#6366f1',
    secColor: '#8b5cf6',
    logo: null,
  },
  
  // CSM info
  csm: null,
  
  // UI states
  isLoading: false,
  isSaving: false,
  saveStatus: 'idle', // 'idle' | 'saving' | 'saved' | 'error'
  error: null,
  
  // Meta
  startedAt: null,
  lastActivityAt: null,
};

const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    // Local state updates (optimistic)
    setCurrentState: (state, action) => {
      state.currentState = action.payload;
    },
    setMaxReachedState: (state, action) => {
      state.maxReachedState = action.payload;
    },
    updateQualification: (state, action) => {
      state.qualification = { ...state.qualification, ...action.payload };
    },
    updateDiagnosticAnswer: (state, action) => {
      const { questionId, answer } = action.payload;
      state.diagnostic.answers[questionId] = answer;
    },
    setDiagnosticQuestion: (state, action) => {
      if (!state.diagnostic) {
        state.diagnostic = {
          questions: [],
          scope: [],
          currentQuestionIndex: 0,
          answers: {},
          results: null,
        };
      }
      state.diagnostic.currentQuestionIndex = action.payload;
    },
    toggleAddon: (state, action) => {
      const addonId = action.payload;
      const index = state.pricing.selectedAddons.indexOf(addonId);
      if (index > -1) {
        state.pricing.selectedAddons.splice(index, 1);
      } else {
        state.pricing.selectedAddons.push(addonId);
      }
    },
    updateConsent: (state, action) => {
      const { type, value } = action.payload;
      state.signature.consents[type] = value;
    },
    updateWorkflow: (state, action) => {
      const { workflowId, enabled } = action.payload;
      const workflow = state.governance.workflows.find(w => w.id === workflowId);
      if (workflow) {
        workflow.enabled = enabled;
      }
    },
    setSaveStatus: (state, action) => {
      state.saveStatus = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    updateBrandingLogo: (state, action) => {
      state.branding.logo = action.payload;
    },
    updateSession: (state, action) => {
      state.session = { ...state.session, ...action.payload };
    },
    setProjects: (state, action) => {
      state.projects = Array.isArray(action.payload) ? action.payload : [];
    },
    resetOnboarding: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch onboarding
      .addCase(fetchOnboarding.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOnboarding.fulfilled, (state, action) => {
        state.isLoading = false;
        const data = action.payload;

        // Champs d'accès (toujours présents)
        state.locked = !!data.locked;
        state.authMethod = data.authMethod || null;
        state.businessId = data.businessId || null;
        state.hasAccount = !!data.hasAccount;
        state.onboardingId = data.id;
        state.currentState = data.currentState;
        state.userData = data.userData || state.userData;
        state.branding = data.branding || state.branding;
        state.csm = data.csm ?? state.csm;

        // Payload verrouillé (non authentifié): on n'écrase pas le reste de l'état
        if (data.locked) {
          return;
        }

        state.companyData.name = data.companyData.name;
        state.maxReachedState = data.maxReachedState || data.currentState;
        state.companyData = data.companyData;
        state.qualification = data.qualification || state.qualification;
        
        // Merge diagnostic data
        if (data.diagnostic) {
          state.diagnostic = {
            ...data.diagnostic,
            // Only preserve local currentQuestionIndex if backend doesn't provide one
            currentQuestionIndex: data.diagnostic.currentQuestionIndex !== undefined 
              ? data.diagnostic.currentQuestionIndex 
              : (state.diagnostic?.currentQuestionIndex ?? 0),
            // Merge answers, local takes precedence
            answers: {
              ...(data.diagnostic.answers || {}),
              ...(state.diagnostic?.answers || {}),
            },
          };
        } else if (!state.diagnostic) {
          state.diagnostic = {
            questions: [],
            scope: [],
            currentQuestionIndex: 0,
            answers: {},
            results: null,
          };
        }
        
        state.blueprint = data.blueprint || state.blueprint;
        state.structures = data.structures || state.structures;
        state.modules = data.modules || state.modules;
        state.automations = data.automations || state.automations;
        state.elements = data.elements || state.elements;
        state.integrations = data.integrations || state.integrations;
        state.projects = data.projects || state.projects;
        state.projectIds = data.projectIds || state.projectIds || [];
        state.mainProjectId = data.mainProjectId || state.mainProjectId || null;
        state.locations = data.locations || state.locations;
        state.pdfs = data.pdfs || state.pdfs;
        state.pricing = data.pricing || state.pricing;
        state.nau = data.nau || state.nau;
        state.signature = data.signature || state.signature;
        state.progress = data.progress || state.progress;
        state.optimization = data.optimization || state.optimization;
        state.governance = data.governance || state.governance;
        state.completion = data.completion;
        state.payment = data.payment || state.payment;
        state.session = data.session || state.session;
        state.branding = data.branding || state.branding;
        state.csm = data.csm;
        state.startedAt = data.startedAt;
        state.lastActivityAt = data.lastActivityAt;
      })
      .addCase(fetchOnboarding.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Transition state
      .addCase(transitionState.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(transitionState.fulfilled, (state, action) => {
        state.isSaving = false;
        state.currentState = action.payload.newState;
        if (action.payload.maxReachedState) {
          state.maxReachedState = action.payload.maxReachedState;
        }
        state.lastActivityAt = new Date().toISOString();
        
        // Update any additional data returned
        if (action.payload.data) {
          Object.assign(state, action.payload.data);
        }
      })
      .addCase(transitionState.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      })
      
      // Navigate to state
      .addCase(navigateToState.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(navigateToState.fulfilled, (state, action) => {
        state.isSaving = false;
        state.currentState = action.payload.currentState;
        if (action.payload.maxReachedState) {
          state.maxReachedState = action.payload.maxReachedState;
        }
        state.lastActivityAt = new Date().toISOString();
      })
      .addCase(navigateToState.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      })
      
      // Save data
      .addCase(saveOnboardingData.pending, (state) => {
        state.saveStatus = 'saving';
      })
      .addCase(saveOnboardingData.fulfilled, (state) => {
        state.saveStatus = 'saved';
        state.lastActivityAt = new Date().toISOString();
      })
      .addCase(saveOnboardingData.rejected, (state, action) => {
        state.saveStatus = 'error';
        state.error = action.payload;
      })

      // Update project child task (BOS)
      .addCase(updateProjectTask.fulfilled, (state, action) => {
        const { projectId, task } = action.payload || {};
        if (!task?.id) return;
        state.projects = (state.projects || []).map((p) => {
          if (projectId && p.id !== projectId) return p;
          const requests = (p.requests || p.children || []).map((t) =>
            t.id === task.id ? { ...t, ...task } : t
          );
          return { ...p, requests, children: requests };
        });
        state.lastActivityAt = new Date().toISOString();
      })
      .addCase(updateProjectTask.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(updateProjectLinks.fulfilled, (state, action) => {
        const { projectId, taskId, data } = action.payload || {};
        if (data?.project?.id) {
          const next = data.project;
          state.projects = (state.projects || []).map((p) =>
            p.id === next.id ? { ...p, ...next } : p
          );
        } else if (taskId && data?.task) {
          const task = data.task;
          const parentId = projectId || task.projectId;
          state.projects = (state.projects || []).map((p) => {
            if (parentId && p.id !== parentId) return p;
            const patchList = (list) =>
              (list || []).map((t) => (t.id === task.id ? { ...t, ...task } : t));
            return {
              ...p,
              internalTasks: patchList(p.internalTasks),
              clientTasks: patchList(p.clientTasks),
              requests: patchList(p.requests || p.children),
              children: patchList(p.children || p.requests),
            };
          });
        }
        state.lastActivityAt = new Date().toISOString();
      })
      .addCase(updateProjectLinks.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Service tickets
      .addCase(fetchServiceTickets.pending, (state) => {
        state.ticketsLoading = true;
      })
      .addCase(fetchServiceTickets.fulfilled, (state, action) => {
        state.ticketsLoading = false;
        state.tickets = action.payload || [];
      })
      .addCase(fetchServiceTickets.rejected, (state) => {
        // Ne pas écraser state.error (sinon l'écran portail entier plante)
        state.ticketsLoading = false;
        state.tickets = state.tickets || [];
      })
      .addCase(createServiceTicket.fulfilled, (state, action) => {
        const created = action.payload;
        if (!created) return;
        const ticket = {
          id: created.elementId,
          name: created.name || '',
          priority: created.priority || 'medium',
          category: created.category || null,
          status: created.status ?? 0,
          note: created.note || '',
          links: created.links || [],
          files: created.files || [],
          projectId: created.projectId || null,
          timeStamp: Math.floor(Date.now() / 1000),
        };
        state.tickets = [ticket, ...(state.tickets || [])];
        state.lastActivityAt = new Date().toISOString();
      })
      .addCase(createServiceTicket.rejected, () => {
        // Erreur locale (toast/modal) — ne pas planter l'écran portail
      })
      .addCase(refreshProjectChildren.fulfilled, (state, action) => {
        const { projectId, clientTasks, internalTasks, tickets } = action.payload || {};
        if (!projectId) return;
        state.projects = (state.projects || []).map((p) => {
          if (p.id !== projectId) return p;
          return {
            ...p,
            clientTasks: clientTasks || p.clientTasks || [],
            requests: clientTasks || p.requests || [],
            internalTasks: internalTasks || p.internalTasks || [],
            tickets: tickets || p.tickets || [],
          };
        });
      });
  },
});

export const {
  setCurrentState,
  setMaxReachedState,
  updateQualification,
  updateDiagnosticAnswer,
  setDiagnosticQuestion,
  toggleAddon,
  updateConsent,
  updateWorkflow,
  setSaveStatus,
  clearError,
  updateBrandingLogo,
  resetOnboarding,
  updateSession,
  setProjects,
} = onboardingSlice.actions;

// Selectors
export const selectOnboarding = (state) => state.onboarding;
export const selectCurrentState = (state) => state.onboarding.currentState;
export const selectMaxReachedState = (state) => state.onboarding.maxReachedState;
export const selectLocked = (state) => state.onboarding.locked;
export const selectAuthMethod = (state) => state.onboarding.authMethod;
export const selectOnboardingBusinessId = (state) => state.onboarding.businessId;
export const selectHasAccount = (state) => state.onboarding.hasAccount;
export const selectIsLoading = (state) => state.onboarding.isLoading;
export const selectError = (state) => state.onboarding.error;
export const selectUserData = (state) => state.onboarding.userData;
export const selectCompanyData = (state) => state.onboarding.companyData;
export const selectQualification = (state) => state.onboarding.qualification;
export const selectDiagnostic = (state) => state.onboarding.diagnostic;
export const selectBlueprint = (state) => state.onboarding.blueprint;
export const selectStructures = (state) => state.onboarding.structures;
export const selectModules = (state) => state.onboarding.modules;
export const selectAutomations = (state) => state.onboarding.automations;
export const selectElements = (state) => state.onboarding.elements;
export const selectIntegrations = (state) => state.onboarding.integrations;
export const selectProjects = (state) => state.onboarding.projects;
export const selectLocations = (state) => state.onboarding.locations;
export const selectPdfs = (state) => state.onboarding.pdfs;
export const selectTickets = (state) => state.onboarding.tickets;
export const selectTicketsLoading = (state) => state.onboarding.ticketsLoading;
export const selectNAU = (state) => state.onboarding.nau;
export const selectPricing = (state) => state.onboarding.pricing;
export const selectSignature = (state) => state.onboarding.signature;
export const selectProgress = (state) => state.onboarding.progress;
export const selectOptimization = (state) => state.onboarding.optimization;
export const selectGovernance = (state) => state.onboarding.governance;
export const selectCompletion = (state) => state.onboarding.completion;
export const selectPayment = (state) => state.onboarding.payment;
export const selectSession = (state) => state.onboarding.session;
export const selectCSM = (state) => state.onboarding.csm;
export const selectBranding = (state) => state.onboarding.branding;
export const selectSaveStatus = (state) => state.onboarding.saveStatus;

export default onboardingSlice.reducer;

